import type { KeywordVolumeRow } from "@/lib/keyword-volume-parser";

export interface BatchFile {
  fileName: string;
  rows: KeywordVolumeRow[];
  /** Confirmed M1 month, "YYYY-MM-01". */
  m1Month: string;
}

export interface BatchDeps {
  upload: (
    name: string,
    m1Month: string,
    rows: KeywordVolumeRow[],
    onProgress: (done: number) => void,
  ) => Promise<number>;
  finish: (uploadId: number) => Promise<void>;
}

export interface BatchResult {
  uploaded: string[];
  failed?: { fileName: string; error: string };
}

/**
 * Uploads files one after another, then rebuilds the summary ONCE. Order does
 * not matter for the data: the database keeps each month from the upload with
 * the latest M1. A failure stops the batch, but what was uploaded before it is
 * still finished, so the page shows it.
 */
export async function uploadBatch(
  files: BatchFile[],
  deps: BatchDeps,
  onProgress?: (doneRows: number, totalRows: number, fileIndex: number) => void,
): Promise<BatchResult> {
  const totalRows = files.reduce((n, f) => n + f.rows.length, 0);
  const uploaded: string[] = [];
  let lastId: number | null = null;
  let before = 0;
  let failed: BatchResult["failed"];

  for (const [i, f] of files.entries()) {
    try {
      lastId = await deps.upload(f.fileName, f.m1Month, f.rows, (done) =>
        onProgress?.(before + done, totalRows, i),
      );
      uploaded.push(f.fileName);
      before += f.rows.length;
    } catch (e) {
      failed = { fileName: f.fileName, error: e instanceof Error ? e.message : String(e) };
      break;
    }
  }

  if (lastId !== null) await deps.finish(lastId);
  return { uploaded, failed };
}
